import { motion } from 'framer-motion';
import { Star, MapPin, ArrowRight, Phone } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { BASE_URL } from '../../../Api/Config';

const RestaurantCard = ({ restaurant, index }) => {
  return (
    <Link to={`/restaurantMenu/${restaurant._id}`}>
      <motion.div
        initial={{ y: 30, opacity: 0 }}
        whileInView={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, delay: index * 0.1 }}
        viewport={{ once: true }}
        whileHover={{ y: -5 }}
        className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden group">
        <div className="relative overflow-hidden">
          <img
            src={restaurant.image}
            alt={restaurant.name}
            className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
            onError={(e) => {
              // Fallback to a default image if API image fails
              e.target.src = '/default-restaurant-image.jpg';
            }}
          />
          <div className="absolute top-4 right-4">
            <span className={`px-3 py-1 rounded-full text-sm font-semibold ${restaurant.isOpen
              ? 'bg-green-600/80 text-snowtxt'
              : 'bg-red-600/80 text-snowtxt'
              }`}>
              {restaurant.isOpen ? 'Open' : 'Closed'}
            </span>
          </div>
          {!restaurant.isActive && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
              <span className="bg-red-600 text-snowtxt text-sm font-semibold px-3 py-1 rounded-lg">
                Temporarily Unavailable
              </span>
            </div>
          )}
        </div>

        <div className="p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-darktxt group-hover:text-redtxt text-base md:text-lg font-semibold transition-colors duration-300">
              {restaurant.name}
            </h3>

            {/* Using default rating since it's not in API response */}
            <span className="inline-flex items-center bg-green-600 text-snowtxt text-xs font-semibold px-2.5 py-1 rounded-lg shadow-sm">
              <Star className="w-3.5 h-3.5 fill-current text-yellow-300" />4.0
            </span>
          </div>

          {/* <p className="text-lightGray text-sm mb-3">
            {restaurant.cuisine}
          </p> */}

          <div className="flex items-center gap-2 text-lightGray text-sm md:text-base mb-2">
            <MapPin className="w-4 h-4" />
            <span className="truncate">{restaurant.address}</span>
          </div>

          <div className="flex items-center gap-2 text-lightGray text-sm md:text-base mb-2">
            <Phone className="w-4 h-4" />
            <span>{restaurant.phone}</span>
          </div>
        </div>
      </motion.div>
    </Link>
  );
};

const HomeRestaurant = () => {
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.6 } },
  };

  useEffect(() => {
    const fetchRestaurants = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${BASE_URL}/public/restaurants/featured`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setRestaurants(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching restaurants:', err);
        setError('Failed to load restaurants. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchRestaurants();
  }, []);

  if (loading) {
    return (
      <section className="py-14 md:py-16 px-4 bg-snowbg">
        <div className="container mx-auto max-w-6xl">
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12">
            <h2 className="text-darktxt text-2xl md:text-4xl font-bold mb-4">
              Your Favorite Campus Spots
            </h2>
            <h3 className="text-lightGray text-base md:text-lg font-semibold max-w-2xl mx-auto">
              Discover tasty meals from all your favorite campus counters
            </h3>
          </motion.div>

          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-redtxt mx-auto"></div>
            <p className="mt-4 text-lightGray">Loading restaurants...</p>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-14 md:py-16 px-4 bg-snowbg">
        <div className="container mx-auto max-w-6xl">
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12">
            <h2 className="text-darktxt text-2xl md:text-4xl font-bold mb-4">
              Your Favorite Campus Spots
            </h2>
            <h3 className="text-lightGray text-base md:text-lg font-semibold max-w-2xl mx-auto">
              Discover tasty meals from all your favorite campus counters
            </h3>
          </motion.div>

          <div className="text-center">
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 max-w-md mx-auto">
              {error}
            </div>
            <button
              onClick={() => window.location.reload()}
              className="bg-redtxt text-white px-4 py-2 rounded hover:bg-red-600 transition-colors">
              Try Again
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-14 md:py-16 px-4 bg-snowbg">
      <div className="container mx-auto max-w-6xl">
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-12">
          <h2 className="text-darktxt text-2xl md:text-4xl font-bold mb-4">
            Your Favorite Campus Spots
          </h2>
          <h3 className="text-lightGray text-base md:text-lg font-semibold max-w-2xl mx-auto">
            Discover tasty meals from all your favorite campus counters
          </h3>
        </motion.div>

        {restaurants.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-lightGray text-lg">No restaurants available at the moment.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {restaurants.map((restaurant, index) => (
              <RestaurantCard
                key={restaurant._id}
                restaurant={restaurant}
                index={index}
              />
            ))}
          </div>
        )}

        <motion.div
          className="mt-12 flex justify-center"
          initial="hidden"
          whileInView="visible"
          variants={itemVariants}
          viewport={{ once: true }}>
          <Link
            to='/restaurants'
            className="max-sm:w-full flex items-center justify-center bg-redtxt text-white text-sm md:text-base font-semibold px-8 py-3 rounded-lg shadow-md hover:shadow-lg transition-all hover:bg-red-600">
            View All Restaurants
            <ArrowRight className="ml-2 w-5 h-5" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default HomeRestaurant;